/**
 * 登录页面
 * 大满书院风格设计
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Lock, User, Eye, EyeOff, ArrowRight, X, KeyRound } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { api, getErrorMessage } from '@/services/api';
import type { ApiResponse, TokenResponse } from '@/types/api';
import { useToast } from '@/components/Toast';
import { getFunctionEntryRoute } from '@/utils/functionEntry';
import styles from './Login.module.css';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, switchFunction } = useAuth();
  const { showToast } = useToast();

  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // 检测会话过期或被踢下线提示
  useEffect(() => {
    const sessionKicked = localStorage.getItem('session_kicked');
    if (sessionKicked === 'true') {
      localStorage.removeItem('session_kicked');
      setError('您的账号已在其他设备登录，当前会话已失效');
    } else {
      const sessionExpired = localStorage.getItem('session_expired');
      if (sessionExpired === 'true') {
        localStorage.removeItem('session_expired');
        setError('登录会话已过期（超过8小时），请重新登录');
      }
    }
  }, []);

  // 修改密码弹窗状态
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    account: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [changePasswordFocusedField, setChangePasswordFocusedField] = useState<string | null>(null);
  // 首次登录强制修改密码弹窗状态
  const [showForceChangePassword, setShowForceChangePassword] = useState(false);
  const [forceChangePasswordData, setForceChangePasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [forceChangePasswordError, setForceChangePasswordError] = useState('');
  const [isForceChangingPassword, setIsForceChangingPassword] = useState(false);
  const [forceChangeFocusedField, setForceChangeFocusedField] = useState<string | null>(null);
  const [showForceNewPassword, setShowForceNewPassword] = useState(false);
  const [showForceConfirmPassword, setShowForceConfirmPassword] = useState(false);
  const [loggedInAccount, setLoggedInAccount] = useState('');
  // 手机验证码登录
  const [loginMode, setLoginMode] = useState<'password'|'phone'>('password');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSending, setCodeSending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const sendCode = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (countdown > 0 || codeSending) return;
    if (!phone || phone.length !== 11) { setError('请输入正确的手机号'); return; }
    setCodeSending(true); setError('');
    try {
      const r = await api.request('/auth/send-code', { method: 'POST', body: JSON.stringify({ phone }) });
      if (r.code === 200) { showToast('success', '验证码已发送'); setCountdown(60); const t = setInterval(() => setCountdown(prev => { if (prev<=1) { clearInterval(t); return 0; } return prev-1; }), 1000); }
      else { setError(r.message || '发送失败'); }
    } catch (e) { setError('发送验证码失败'); }
    finally { setCodeSending(false); }
  };

  // 防重复提交：登录+进入功能的整个流程期间禁止再次提交
  // （否则连点两次登录会在单会话机制下互相顶掉会话，出现"已在其他设备登录"自踢）
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      await doLogin();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const doLogin = async () => {

    if (loginMode === 'phone') {
      if (!phone || phone.length !== 11) { setError('请输入正确的手机号'); return; }
      if (!code || code.length < 4) { setError('请输入验证码'); return; }
    } else {
      if (!account.trim()) { setError('请输入账号'); return; }
      if (!password.trim()) { setError('请输入密码'); return; }
    }

    try {
      let tokenResponse: TokenResponse;
      if (loginMode === 'phone') {
        // 手机号登录返回的是 ResponseModel<TokenResponse> 格式，需要提取 data
        const resp = await api.request<ApiResponse<TokenResponse>>('/auth/login', { method: 'POST', body: JSON.stringify({ phone, code }) });
        if (resp.code !== 200) { setError(resp.message || '登录失败'); return; }
        tokenResponse = resp.data!;
      } else {
        tokenResponse = await login(account, password);
      }
      if (loginMode === 'phone') {
        const td = tokenResponse;
        // 存入 token 和过期时间
        api.setToken(td.access_token);
        localStorage.setItem('refresh_token', td.refresh_token);
        localStorage.setItem('token_expires_at', String(Date.now() + td.expires_in * 1000));
        localStorage.setItem('user', JSON.stringify({ user_id: 0, user_name: '', role_name: '客户' }));
        localStorage.setItem('currentFunction', 'share');
        localStorage.setItem('currentFunctionRole', '客户');
        window.location.href = window.innerWidth <= 768 ? '/share/feed' : '/share/browse';
        return;
      }

      // 检查是否首次登录
      if (tokenResponse.is_first_login) {
        navigate('/force-change-password', { state: { account } });
        return;
      }

      // 根据 need_select 决定跳转；仅授权一个功能模块时自动直接进入
      const uniqueFuncs = new Set((tokenResponse.functions || []).map(f => f.function));
      if (tokenResponse.need_select && uniqueFuncs.size > 1) {
        navigate('/select-function');
      } else if (tokenResponse.functions && tokenResponse.functions.length > 0) {
        const fn = tokenResponse.functions[0];
        // 直进也必须建立功能上下文（角色名/权限），否则侧边栏按角色过滤的菜单不显示
        try { await switchFunction(fn.function, fn.role_id); } catch { /* 上下文建立失败时仍直接进入 */ }
        navigate(getFunctionEntryRoute(fn.function, fn.role_name || ''));
      } else {
        navigate('/');
      }
    } catch (err) {
      // 显示后端返回的具体错误信息
      const message = err instanceof Error ? err.message : '登录失败，请重试';
      // 如果是账户被禁用，显示更友好的提示
      if (message.includes('禁用')) {
        setError('您的账户已被禁用，请联系管理员处理');
      } else {
        setError(message);
      }
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePasswordError('');

    if (!changePasswordData.account.trim()) {
      setChangePasswordError('请输入账号');
      return;
    }

    if (!changePasswordData.oldPassword.trim()) {
      setChangePasswordError('请输入原密码');
      return;
    }

    if (!changePasswordData.newPassword.trim()) {
      setChangePasswordError('请输入新密码');
      return;
    }

    if (changePasswordData.newPassword.length < 6) {
      setChangePasswordError('新密码长度至少6位');
      return;
    }

    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setChangePasswordError('两次输入的新密码不一致');
      return;
    }

    setIsChangingPassword(true);
    try {
      // 先登录验证账号和原密码
      await login(changePasswordData.account, changePasswordData.oldPassword);
      // 登录成功后调用修改密码API
      const res = await api.changePassword(changePasswordData.oldPassword, changePasswordData.newPassword);
      if (res.code === 200) {
        showToast('success', '密码修改成功，请使用新密码登录');
        setShowChangePassword(false);
        setChangePasswordData({
          account: '',
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        // 清空登录表单
        setAccount('');
        setPassword('');
      } else {
        setChangePasswordError(res.message || '密码修改失败');
      }
    } catch (err) {
      const message = getErrorMessage(err);
      if (message.includes('密码') || message.includes('账号')) {
        setChangePasswordError('账号或原密码错误');
      } else {
        setChangePasswordError(message);
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const closeChangePasswordModal = () => {
    if (!isChangingPassword) {
      setShowChangePassword(false);
      setChangePasswordError('');
      setChangePasswordData({
        account: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  };

  // 首次登录强制修改密码提交
  const handleForceChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForceChangePasswordError('');

    if (!forceChangePasswordData.newPassword.trim()) {
      setForceChangePasswordError('请输入新密码');
      return;
    }

    if (forceChangePasswordData.newPassword.length < 6) {
      setForceChangePasswordError('新密码长度至少6位');
      return;
    }

    if (forceChangePasswordData.newPassword !== forceChangePasswordData.confirmPassword) {
      setForceChangePasswordError('两次输入的密码不一致');
      return;
    }

    setIsForceChangingPassword(true);
    try {
      // 调用修改密码API（首次登录时使用默认密码123456作为旧密码）
      const res = await api.changePassword('123456', forceChangePasswordData.newPassword);
      if (res.code === 200) {
        showToast('success', '密码修改成功，正在跳转...');
        setShowForceChangePassword(false);
        // 延迟跳转到首页
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setForceChangePasswordError(res.message || '密码修改失败');
      }
    } catch (err) {
      const message = getErrorMessage(err);
      setForceChangePasswordError(message);
    } finally {
      setIsForceChangingPassword(false);
    }
  };

  const isWechat = /micromessenger/i.test(navigator.userAgent);

  return (
    <div className={styles.container}>
      {isWechat && (
        <div className={styles.wechatBanner}>
          <X size={20} style={{ cursor: 'pointer' }} onClick={() => {/* dismissable via state */}} />
          <div>
            <strong>请在浏览器中打开</strong>
            <p>微信内暂不支持语音录入，请点击右上角「在浏览器中打开」</p>
          </div>
        </div>
      )}
      {/* 背景装饰 */}
      <div className={styles.background}>
        <div className={styles.inkWash1} />
        <div className={styles.inkWash2} />
        <div className={styles.inkWash3} />
      </div>

      {/* 登录卡片 */}
      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* 头部 */}
        <div className={styles.header}>
          <motion.div
            className={styles.logo}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <BookOpen size={32} strokeWidth={1.5} />
          </motion.div>
          <motion.h1
            className={styles.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            大满书院
          </motion.h1>
          <motion.p
            className={styles.subtitle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            知识传承系统
          </motion.p>
        </div>

        {/* 表单 */}
        <motion.form
          className={styles.form}
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          {/* 登录方式切换 */}
          <div className={styles.loginTabs}>
            <button type="button" className={`${styles.loginTab} ${loginMode==='password'?styles.loginTabActive:''}`} onClick={()=>setLoginMode('password')}>账号登录</button>
            <button type="button" className={`${styles.loginTab} ${loginMode==='phone'?styles.loginTabActive:''}`} onClick={()=>setLoginMode('phone')}>手机登录</button>
          </div>

          {loginMode === 'password' ? <>
          <div className={`${styles.inputGroup} ${focusedField==='account'?styles.focused:''}`}>
            <div className={styles.inputIcon}><User size={18} strokeWidth={1.5}/></div>
            <input type="text" placeholder="请输入账号" value={account} onChange={(e)=>setAccount(e.target.value)} onFocus={()=>setFocusedField('account')} onBlur={()=>setFocusedField(null)} autoComplete="username"/>
          </div>
          <div className={`${styles.inputGroup} ${focusedField==='password'?styles.focused:''}`}>
            <div className={styles.inputIcon}><Lock size={18} strokeWidth={1.5}/></div>
            <input type={showPassword?'text':'password'} placeholder="请输入密码" value={password} onChange={(e)=>setPassword(e.target.value)} onFocus={()=>setFocusedField('password')} onBlur={()=>setFocusedField(null)} autoComplete="current-password"/>
            <button type="button" className={styles.togglePassword} onClick={()=>setShowPassword(!showPassword)} tabIndex={-1}>
              {showPassword?<EyeOff size={18} strokeWidth={1.5}/>:<Eye size={18} strokeWidth={1.5}/>}
            </button>
          </div>
          </> : <>
          <div className={`${styles.inputGroup} ${focusedField==='phone'?styles.focused:''}`}>
            <div className={styles.inputIcon}><User size={18} strokeWidth={1.5}/></div>
            <input type="tel" placeholder="请输入手机号" maxLength={11} value={phone} onChange={(e)=>setPhone(e.target.value.replace(/\D/g,''))} onFocus={()=>setFocusedField('phone')} onBlur={()=>setFocusedField(null)} autoComplete="tel"/>
          </div>
          <div className={`${styles.inputGroup} ${focusedField==='code'?styles.focused:''}`}>
            <div className={styles.inputIcon}><Lock size={18} strokeWidth={1.5}/></div>
            <input type="text" placeholder="请输入验证码" maxLength={6} value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g,''))} onFocus={()=>setFocusedField('code')} onBlur={()=>setFocusedField(null)} autoComplete="one-time-code"/>
            <button type="button" className={styles.codeBtn} onClick={sendCode} disabled={countdown>0||codeSending} style={{whiteSpace:'nowrap',padding:'6px 14px',borderRadius:6,border:'1px solid var(--cinnabar-300)',background:'var(--cinnabar-50)',color:'var(--cinnabar-600)',cursor:countdown>0?'default':'pointer',fontSize:'.75rem'}}>
              {countdown>0?`${countdown}s`:'获取验证码'}
            </button>
          </div>
          </>}

          {/* 错误提示 */}
          {error&&<motion.div className={styles.error} initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}}>{error}</motion.div>}

          {/* 登录按钮 */}
          <motion.button type="submit" className={styles.submitButton} disabled={isLoading || submitting} whileHover={{scale:1.01}} whileTap={{scale:.99}}>
            {(isLoading||submitting)?<span className={styles.loading}>登录中...</span>:<><span>登录</span><ArrowRight size={18} strokeWidth={2}/></>}
          </motion.button>
        </motion.form>

        {/* 提示 */}
        <motion.div
          className={styles.hint}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          {loginMode === 'password'
            ? <p>忘记密码？请联系管理员或对接人进行重置</p>
            : <p>客户请使用手机验证码登录</p>
          }
          <button
            type="button"
            className={styles.changePasswordLink}
            onClick={() => setShowChangePassword(true)}
          >
            <KeyRound size={14} />
            修改密码
          </button>
        </motion.div>
      </motion.div>

      {/* 修改密码弹窗 */}
      <AnimatePresence>
        {showChangePassword && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeChangePasswordModal}
          >
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 弹窗头部 */}
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>修改密码</h2>
                <button
                  type="button"
                  className={styles.modalClose}
                  onClick={closeChangePasswordModal}
                  disabled={isChangingPassword}
                >
                  <X size={20} />
                </button>
              </div>

              {/* 弹窗表单 */}
              <form className={styles.modalForm} onSubmit={handleChangePassword}>
                {/* 账号输入 */}
                <div
                  className={`${styles.inputGroup} ${changePasswordFocusedField === 'account' ? styles.focused : ''}`}
                >
                  <div className={styles.inputIcon}>
                    <User size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type="text"
                    placeholder="请输入账号"
                    value={changePasswordData.account}
                    onChange={(e) => setChangePasswordData({ ...changePasswordData, account: e.target.value })}
                    onFocus={() => setChangePasswordFocusedField('account')}
                    onBlur={() => setChangePasswordFocusedField(null)}
                    disabled={isChangingPassword}
                    autoComplete="username"
                  />
                </div>

                {/* 原密码输入 */}
                <div
                  className={`${styles.inputGroup} ${changePasswordFocusedField === 'oldPassword' ? styles.focused : ''}`}
                >
                  <div className={styles.inputIcon}>
                    <Lock size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type={showOldPassword ? 'text' : 'password'}
                    placeholder="请输入原密码"
                    value={changePasswordData.oldPassword}
                    onChange={(e) => setChangePasswordData({ ...changePasswordData, oldPassword: e.target.value })}
                    onFocus={() => setChangePasswordFocusedField('oldPassword')}
                    onBlur={() => setChangePasswordFocusedField(null)}
                    disabled={isChangingPassword}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className={styles.togglePassword}
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    tabIndex={-1}
                    disabled={isChangingPassword}
                  >
                    {showOldPassword ? (
                      <EyeOff size={18} strokeWidth={1.5} />
                    ) : (
                      <Eye size={18} strokeWidth={1.5} />
                    )}
                  </button>
                </div>

                {/* 新密码输入 */}
                <div
                  className={`${styles.inputGroup} ${changePasswordFocusedField === 'newPassword' ? styles.focused : ''}`}
                >
                  <div className={styles.inputIcon}>
                    <KeyRound size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="请输入新密码（至少6位）"
                    value={changePasswordData.newPassword}
                    onChange={(e) => setChangePasswordData({ ...changePasswordData, newPassword: e.target.value })}
                    onFocus={() => setChangePasswordFocusedField('newPassword')}
                    onBlur={() => setChangePasswordFocusedField(null)}
                    disabled={isChangingPassword}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className={styles.togglePassword}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex={-1}
                    disabled={isChangingPassword}
                  >
                    {showNewPassword ? (
                      <EyeOff size={18} strokeWidth={1.5} />
                    ) : (
                      <Eye size={18} strokeWidth={1.5} />
                    )}
                  </button>
                </div>

                {/* 确认新密码输入 */}
                <div
                  className={`${styles.inputGroup} ${changePasswordFocusedField === 'confirmPassword' ? styles.focused : ''}`}
                >
                  <div className={styles.inputIcon}>
                    <KeyRound size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="请再次输入新密码"
                    value={changePasswordData.confirmPassword}
                    onChange={(e) => setChangePasswordData({ ...changePasswordData, confirmPassword: e.target.value })}
                    onFocus={() => setChangePasswordFocusedField('confirmPassword')}
                    onBlur={() => setChangePasswordFocusedField(null)}
                    disabled={isChangingPassword}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className={styles.togglePassword}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                    disabled={isChangingPassword}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} strokeWidth={1.5} />
                    ) : (
                      <Eye size={18} strokeWidth={1.5} />
                    )}
                  </button>
                </div>

                {/* 错误提示 */}
                {changePasswordError && (
                  <motion.div
                    className={styles.error}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {changePasswordError}
                  </motion.div>
                )}

                {/* 提交按钮 */}
                <motion.button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isChangingPassword}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {isChangingPassword ? (
                    <span className={styles.loading}>修改中...</span>
                  ) : (
                    <>
                      <span>确认修改</span>
                      <ArrowRight size={18} strokeWidth={2} />
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 首次登录强制修改密码弹窗 */}
      <AnimatePresence>
        {showForceChangePassword && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 弹窗头部 */}
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>首次登录，请修改密码</h2>
              </div>

              <div className={styles.forceChangeHint}>
                <p>这是您首次登录系统，为了账号安全，请先修改默认密码。</p>
              </div>

              {/* 弹窗表单 */}
              <form className={styles.modalForm} onSubmit={handleForceChangePassword}>
                {/* 新密码输入 */}
                <div
                  className={`${styles.inputGroup} ${forceChangeFocusedField === 'newPassword' ? styles.focused : ''}`}
                >
                  <div className={styles.inputIcon}>
                    <KeyRound size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type={showForceNewPassword ? 'text' : 'password'}
                    placeholder="请输入新密码（至少6位）"
                    value={forceChangePasswordData.newPassword}
                    onChange={(e) => setForceChangePasswordData({ ...forceChangePasswordData, newPassword: e.target.value })}
                    onFocus={() => setForceChangeFocusedField('newPassword')}
                    onBlur={() => setForceChangeFocusedField(null)}
                    disabled={isForceChangingPassword}
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    className={styles.togglePassword}
                    onClick={() => setShowForceNewPassword(!showForceNewPassword)}
                    tabIndex={-1}
                    disabled={isForceChangingPassword}
                  >
                    {showForceNewPassword ? (
                      <EyeOff size={18} strokeWidth={1.5} />
                    ) : (
                      <Eye size={18} strokeWidth={1.5} />
                    )}
                  </button>
                </div>

                {/* 确认新密码输入 */}
                <div
                  className={`${styles.inputGroup} ${forceChangeFocusedField === 'confirmPassword' ? styles.focused : ''}`}
                >
                  <div className={styles.inputIcon}>
                    <KeyRound size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type={showForceConfirmPassword ? 'text' : 'password'}
                    placeholder="请再次输入新密码"
                    value={forceChangePasswordData.confirmPassword}
                    onChange={(e) => setForceChangePasswordData({ ...forceChangePasswordData, confirmPassword: e.target.value })}
                    onFocus={() => setForceChangeFocusedField('confirmPassword')}
                    onBlur={() => setForceChangeFocusedField(null)}
                    disabled={isForceChangingPassword}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className={styles.togglePassword}
                    onClick={() => setShowForceConfirmPassword(!showForceConfirmPassword)}
                    tabIndex={-1}
                    disabled={isForceChangingPassword}
                  >
                    {showForceConfirmPassword ? (
                      <EyeOff size={18} strokeWidth={1.5} />
                    ) : (
                      <Eye size={18} strokeWidth={1.5} />
                    )}
                  </button>
                </div>

                {/* 错误提示 */}
                {forceChangePasswordError && (
                  <motion.div
                    className={styles.error}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {forceChangePasswordError}
                  </motion.div>
                )}

                {/* 提交按钮 */}
                <motion.button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isForceChangingPassword}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {isForceChangingPassword ? (
                    <span className={styles.loading}>修改中...</span>
                  ) : (
                    <>
                      <span>确认修改</span>
                      <ArrowRight size={18} strokeWidth={2} />
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}